/**
 * a4Templates — the registry of A4 invoice layouts, and how one is chosen.
 *
 * A4 previously had exactly one hardcoded layout while the thermal receipt had
 * a designer and presets. This gives A4 the same idea: a named set of layouts
 * and a stored choice, so adding a third is a registry entry rather than an
 * edit to every print path.
 *
 * The stored setting is A4_INVOICE_TEMPLATE. It defaults to 'standard', which
 * is the layout every existing store already prints — a shop mid-pilot must not
 * have its invoices change shape because a new option shipped.
 */

import type { A4ColumnId } from './a4Columns';
import type { InvoiceCopyType, ReceiptData } from '../contracts/ReceiptContracts';
import { buildA4InvoiceHtml } from './a4InvoiceTemplate';
import { buildClassicInvoiceHtml } from './classicInvoiceTemplate';

export type A4TemplateId = 'standard' | 'classic';

export const A4_TEMPLATE_SETTING = 'A4_INVOICE_TEMPLATE';
export const DEFAULT_A4_TEMPLATE: A4TemplateId = 'standard';

/** Setting holding the merchant's chosen item columns, as a JSON id array. */
export const A4_COLUMNS_SETTING = 'A4_INVOICE_COLUMNS';

export interface A4RenderOptions {
  copyType?: InvoiceCopyType;
  printedAt?: Date;
  /** Item columns to print. Only the ruled `classic` layout honours these. */
  columns?: A4ColumnId[];
}

export interface A4TemplateMeta {
  id: A4TemplateId;
  label: string;
  description: string;
  /** Whether this layout prints the statutory copy marking at all. */
  supportsCopyType: boolean;
}

export const A4_TEMPLATES: A4TemplateMeta[] = [
  {
    id: 'standard',
    label: 'Standard',
    description:
      'Open layout with per-line taxable value, GST rate and tax. Easiest to read when a bill has many lines at different rates.',
    supportsCopyType: false,
  },
  {
    id: 'classic',
    label: 'Classic (ruled)',
    description:
      'The boxed, ruled tax invoice familiar from traditional accounting packages. Tax is summarised by HSN at the foot rather than per line, and it carries the bank block and copy marking.',
    supportsCopyType: true,
  },
];

/** Narrow an arbitrary stored string to a known template id. */
export function resolveA4TemplateId(raw: string | null | undefined): A4TemplateId {
  return raw === 'classic' || raw === 'standard' ? raw : DEFAULT_A4_TEMPLATE;
}

/**
 * Render an invoice with the chosen layout.
 *
 * Options that a layout does not support are ignored rather than rejected —
 * a caller offering a copy selector should not have to branch on which
 * template happens to be selected.
 */
export function buildA4Html(
  data: ReceiptData,
  templateId: A4TemplateId = DEFAULT_A4_TEMPLATE,
  options: A4RenderOptions = {},
): string {
  return templateId === 'classic'
    ? buildClassicInvoiceHtml(data, options)
    : buildA4InvoiceHtml(data);
}

/**
 * Every statutory copy of one invoice, as separate documents.
 *
 * Rule 46 asks for a supply of GOODS to be invoiced in triplicate, each copy
 * marked with who it is for. Returned as an array rather than one concatenated
 * document so the caller decides whether that means three pages or three files.
 */
export function buildA4HtmlAllCopies(
  data: ReceiptData,
  templateId: A4TemplateId = DEFAULT_A4_TEMPLATE,
  options: A4RenderOptions = {},
): { copyType: InvoiceCopyType; html: string }[] {
  const copies: InvoiceCopyType[] = ['ORIGINAL', 'DUPLICATE', 'TRIPLICATE'];
  return copies.map((copyType) => ({
    copyType,
    html: buildA4Html(data, templateId, { ...options, copyType }),
  }));
}
